/**
 * Compress/resize an image file for upload so payloads stay under typical
 * reverse-proxy body limits (often ~1MB) while keeping usable quality.
 */

const DEFAULT_MAX_EDGE = 1600;
const DEFAULT_QUALITY = 0.82;
const DEFAULT_MAX_BYTES = 900 * 1024; // aim under ~1MB proxy limits

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read this image. Try JPG or PNG.'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('Failed to compress image'));
        else resolve(blob);
      },
      type,
      quality
    );
  });
}

/**
 * @param {File} file
 * @param {{ maxEdge?: number, quality?: number, maxBytes?: number }} [options]
 * @returns {Promise<File>}
 */
export async function compressImageForUpload(file, options = {}) {
  if (!file || !file.type?.startsWith('image/')) {
    throw new Error('Please select a valid image file');
  }

  // Already small enough — skip work
  if (file.size <= (options.maxBytes || DEFAULT_MAX_BYTES) && file.size <= 500 * 1024) {
    return file;
  }

  const maxEdge = options.maxEdge || DEFAULT_MAX_EDGE;
  let quality = options.quality ?? DEFAULT_QUALITY;
  const maxBytes = options.maxBytes || DEFAULT_MAX_BYTES;

  const img = await loadImageFromFile(file);
  let { width, height } = img;

  if (width > maxEdge || height > maxEdge) {
    const scale = maxEdge / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to compress image');
  ctx.drawImage(img, 0, 0, width, height);

  // Prefer JPEG for photos (much smaller than PNG)
  const outputType = 'image/jpeg';
  let blob = await canvasToBlob(canvas, outputType, quality);

  // Step down quality if still too large
  while (blob.size > maxBytes && quality > 0.5) {
    quality = Math.max(0.5, quality - 0.1);
    blob = await canvasToBlob(canvas, outputType, quality);
  }

  // If still huge, shrink dimensions once more
  if (blob.size > maxBytes && Math.max(width, height) > 1000) {
    const scale = 1000 / Math.max(width, height);
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    blob = await canvasToBlob(canvas, outputType, 0.75);
  }

  const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '') || 'photo';
  return new File([blob], `${baseName}.jpg`, {
    type: outputType,
    lastModified: Date.now(),
  });
}

/**
 * Read upload error body whether JSON or HTML (e.g. nginx 413).
 * @param {Response} response
 * @returns {Promise<string>}
 */
export async function readUploadErrorMessage(response) {
  if (response.status === 413) {
    return 'Photo is too large for the server. It should compress automatically — try again or use a smaller image.';
  }

  const text = await response.text();
  if (!text) {
    return `Failed to upload photo (${response.status})`;
  }

  try {
    const data = JSON.parse(text);
    return data.error || data.details || `Failed to upload photo (${response.status})`;
  } catch {
    if (text.trim().startsWith('<')) {
      return response.status === 413
        ? 'Photo is too large for the server. Try a smaller image.'
        : `Upload failed (${response.status}). Please try a smaller image.`;
    }
    return text.slice(0, 200) || `Failed to upload photo (${response.status})`;
  }
}
